import { createFileRoute } from "@tanstack/react-router";

import { SealButton } from "@/components/site/ctas";
import { ProcessRail } from "@/components/site/home-sections";
import { Reveal } from "@/components/site/motion";
import { getDict, href, type Lang } from "@/content/site";

export const Route = createFileRoute("/$lang/how-we-work")({
  loader: ({ params }) => ({ lang: params.lang as Lang }),
  head: ({ loaderData }) => {
    const t = getDict(loaderData?.lang ?? "en");
    return { meta: [{ title: `${t.how.title} · insidermark` }, { name: "description", content: t.how.intro }] };
  },
  component: Page,
});

function Page() {
  const { lang } = Route.useLoaderData();
  const t = getDict(lang);
  const h = t.how;
  return (
    <main>
      <header className="im-page-head">
        <div className="im-wrap">
          <Reveal>
            <h1 className="im-display">{h.title}</h1>
            <p className="im-body-text mt-5">{h.intro}</p>
          </Reveal>
        </div>
      </header>

      <ProcessRail lang={lang} t={t} />

      <section className="im-section" id="market-representation">
        <div className="im-wrap im-two">
          <Reveal>
            <span className="im-layer__n">01 / 03</span>
            <h2 className="im-display-sm mt-3">{h.representation.title}</h2>
          </Reveal>
          <div className="im-prose">
            {h.representation.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="im-section" id="quality-control">
        <div className="im-plate im-plate--manifest" aria-hidden="true" />
        <div className="im-wrap im-rel im-two">
          <Reveal>
            <span className="im-layer__n">02 / 03</span>
            <h2 className="im-display-sm mt-3">{h.quality.title}</h2>
            <div className="im-prose mt-6">
              {h.quality.body.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
          </Reveal>
          <Reveal axis="x">
            <div className="im-report">
              <div className="im-report__seal">
                insidermark
                <br />
                QC
              </div>
              <dl>
                {h.quality.report.map((r) => (
                  <div key={r.label}>
                    <dt>{r.label}</dt>
                    <dd>{r.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="im-section" id="consolidation-logistics">
        <div className="im-wrap im-two">
          <Reveal>
            <span className="im-layer__n">03 / 03</span>
            <h2 className="im-display-sm mt-3">{h.consolidation.title}</h2>
          </Reveal>
          <div className="im-prose">
            {h.consolidation.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="im-section im-section--tight" id="incoterms-and-payment">
        <div className="im-wrap">
          <Reveal>
            <h2 className="im-display-sm">{h.incoterms.title}</h2>
            <p className="im-body-text mt-4">{h.incoterms.intro}</p>
          </Reveal>
          <div className="im-terms mt-10">
            {h.incoterms.terms.map((term) => (
              <div className="im-term" key={term.code}>
                <b>{term.code}</b>
                <strong>{term.name}</strong>
                <p>{term.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="im-section">
        <div className="im-wrap im-two">
          <Reveal>
            <h2 className="im-display-sm">{h.payment.title}</h2>
          </Reveal>
          <div className="im-prose">
            {h.payment.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="im-section im-section--tight">
        <div className="im-plate im-plate--steel" aria-hidden="true" />
        <div className="im-wrap im-rel">
          <p className="im-mono">{h.faq}</p>
          <h2 className="im-display-sm mt-4 max-w-[22ch]">{h.bypass.q}</h2>
          <p className="im-body-text mt-5">{h.bypass.a}</p>
          <div className="mt-8">
            <SealButton to={href(lang, "/rfq")}>{t.nav.rfq}</SealButton>
          </div>
        </div>
      </section>
    </main>
  );
}
